import { isEmpty } from 'lodash'
import { useContext, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Select, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import {
  AbTestsAddEditContext,
  AbTestsAddEditContextType,
} from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { useFeatureFlag } from 'features/user/hooks/useFeatureFlag'
import { generateSelector } from 'providers/product-fruits/utils'
import { useCanSaveAndStartAvailability } from './hooks/useCanSaveAndStartAvailability'
import { useGetDomainsForSelect } from './hooks/useGetDomainsForSelect'
import { useTrafficSplitTypeOptions } from './hooks/useTrafficSplitTypeOptions'
import { ParametersSection } from './sections/ParametersSection'
import { VariantsSection } from './sections/VariantsSection'

import type { AbTestsCommonSchema } from './abTestsCommonSchema'
import type { AbTestsCommonFormProps } from './types'

export const AbTestsCommonForm = ({ variant }: AbTestsCommonFormProps): JSX.Element => {
  const { t } = useTranslation('features/abTests', { keyPrefix: 'common' })

  const { domains } = useGetDomainsForSelect()
  const {
    isDisabled,
    loading: formLoading,
    setValid,
  } = useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)

  const { control, formState } = useFormContext<AbTestsCommonSchema>()
  const { parameters, variants } = useWatch<AbTestsCommonSchema>()

  useEffect(() => {
    if (parameters === undefined || variants === undefined) {
      setValid(false)
    } else if (isEmpty(formState.errors)) {
      setValid(true)
    }
  }, [formState])

  useCanSaveAndStartAvailability({ domains })

  const { FeatureContainer } = useFeatureFlag('yp2395AbTestsDivideTraffic')
  const isTrafficSplitTypeDisabled: boolean = isDisabled || formLoading

  const trafficSplitTypeOptions = useTrafficSplitTypeOptions({
    disabled: isTrafficSplitTypeDisabled,
  })

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('basicAttributes.subtitle')}
          title={t('basicAttributes.title')}
        />

        <GapBox />

        <BoxForm
          data-product-fruits={
            variant === 'add' ? generateSelector({ step: 2, subTour: 2, tour: 5 }) : undefined
          }
          sx={{ width: 'fit-content' }}
        >
          <TextField
            control={control}
            disabled={isDisabled || formLoading}
            label={t('basicAttributes.name.label')}
            name='name'
          />

          <Select
            control={control}
            disabled={isDisabled || variant === 'edit' || formLoading}
            label={t('basicAttributes.domainId.label')}
            name='domainId'
            options={domains}
          />

          <FeatureContainer>
            <RadioGroup
              control={control}
              disabled={isTrafficSplitTypeDisabled}
              label={t('basicAttributes.trafficSplitType.label')}
              name='trafficSplitType'
              options={trafficSplitTypeOptions}
              tooltip={{ content: t('basicAttributes.trafficSplitType.tooltip') }}
            />
          </FeatureContainer>
        </BoxForm>
      </Paper>

      <ParametersSection variant={variant} />
      {parameters && <VariantsSection variant={variant} />}
    </>
  )
}
